import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * SparkleTrail Component
 * Creates a magical sparkle animation that flows from source to destination
 * when the agent performs navigation actions
 */
const SparkleTrail = ({ 
  isActive, 
  sourceElement, 
  targetElement, 
  onComplete,
  actionType = 'navigate' 
}) => {
  const [sparkles, setSparkles] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  // Generate sparkle particles
  const generateSparkles = (start, end, count = 8) => {
    const sparkleArray = [];
    
    for (let i = 0; i < count; i++) {
      const progress = i / (count - 1);
      const delay = i * 100; // Stagger sparkles
      
      // Calculate position along the path
      const x = start.x + (end.x - start.x) * progress;
      const y = start.y + (end.y - start.y) * progress;
      
      // Add some randomness to make it organic
      const randomOffset = 20;
      const offsetX = (Math.random() - 0.5) * randomOffset;
      const offsetY = (Math.random() - 0.5) * randomOffset;
      
      sparkleArray.push({
        id: `sparkle-${i}`,
        x: x + offsetX,
        y: y + offsetY,
        delay,
        size: Math.random() * 16 + 12, // 12-28px (nice and visible)
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.5 + 0.5 // 0.5-1.0
      });
    }
    
    return sparkleArray;
  };

  // Calculate element positions relative to viewport (for fixed positioned sparkles)
  const getElementPosition = (element) => {
    if (!element) return { x: 0, y: 0 };
    
    const rect = element.getBoundingClientRect();
    
    // For fixed positioned sparkle overlay, we want viewport-relative coordinates
    // getBoundingClientRect() already gives us viewport-relative positions
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  // Start sparkle animation
  useEffect(() => {
    // console.log('🎇 SparkleTrail effect:', { isActive, sourceElement, targetElement, actionType });
    
    if (isActive && sourceElement && targetElement) {
      // console.log('🎇 Starting sparkle animation!');
      
      const sourcePos = getElementPosition(sourceElement);
      const targetPos = getElementPosition(targetElement);
      
      // console.log('🎇 Positions:', { sourcePos, targetPos });
      
      const newSparkles = generateSparkles(sourcePos, targetPos);
      setSparkles(newSparkles);
      setIsAnimating(true);
      
      // console.log('🎇 Generated sparkles:', newSparkles.length);
      
      // Complete animation after sparkles finish
      const animationDuration = newSparkles.length * 100 + 1200; // Stagger + animation time
      setTimeout(() => {
        // console.log('🎇 Animation complete');
        setIsAnimating(false);
        setSparkles([]);
        if (onComplete) onComplete();
      }, animationDuration);
    } else {
      // console.log('🎇 Sparkle animation conditions not met:', {
      //   isActive,
      //   hasSource: !!sourceElement,
      //   hasTarget: !!targetElement
      // });
    }
  }, [isActive, sourceElement, targetElement, onComplete, actionType]);

  // Don't render if not active
  if (!isActive || !isAnimating) return null;

  // Use a portal to render at document root to avoid any container positioning issues
  const sparkleContainer = (
    <div className="fixed inset-0 pointer-events-none z-50">
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute animate-sparkle"
          style={{
            left: sparkle.x,
            top: sparkle.y,
            animationDelay: `${sparkle.delay}ms`,
            '--sparkle-size': `${sparkle.size}px`,
            '--sparkle-rotation': `${sparkle.rotation}deg`,
            '--sparkle-opacity': sparkle.opacity
          }}
        >
          <SparkleIcon />
        </div>
      ))}
      
      {/* Add floating action text */}
      <div 
        className="absolute animate-action-text"
        style={{
          left: sourceElement ? getElementPosition(sourceElement).x : 0,
          top: sourceElement ? getElementPosition(sourceElement).y - 30 : 0,
        }}
      >
        <div className="bg-blue-500/90 text-white text-sm px-3 py-1 rounded-full shadow-lg">
          {getActionText(actionType)}
        </div>
      </div>
      
      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes sparkle {
          0% {
            transform: scale(0) rotate(var(--sparkle-rotation));
            opacity: 0;
          }
          20% {
            transform: scale(1) rotate(calc(var(--sparkle-rotation) + 60deg));
            opacity: var(--sparkle-opacity);
          }
          80% {
            transform: scale(1) rotate(calc(var(--sparkle-rotation) + 180deg));
            opacity: var(--sparkle-opacity);
          }
          100% {
            transform: scale(0) rotate(calc(var(--sparkle-rotation) + 360deg));
            opacity: 0;
          }
        }
        
        @keyframes action-text {
          0% {
            transform: translateY(10px);
            opacity: 0;
          }
          20%, 80% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(-10px);
            opacity: 0;
          }
        }
        
        .animate-sparkle {
          width: var(--sparkle-size);
          height: var(--sparkle-size);
          animation: sparkle 1.2s ease-out forwards;
        }
        
        .animate-action-text {
          animation: action-text 1.5s ease-out forwards;
        }
      `}</style>
    </div>
  );

  // Render using portal to document body to avoid positioning issues
  return createPortal(sparkleContainer, document.body);
};

// Sparkle SVG Icon
const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full text-yellow-400">
    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
  </svg>
);

// Get action text based on type
const getActionText = (actionType) => {
  const actionMessages = {
    navigate: '🎯 Navigating...',
    analyze: '📊 Analyzing...',
    filter: '🔍 Filtering...',
    page: '📄 Opening page...',
    default: '✨ Working...'
  };
  
  return actionMessages[actionType] || actionMessages.default;
};

export default SparkleTrail;
